import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApiService } from './api.service';

export interface PracticeLocationProviderRequest {
  limit?: number;
  locationId?: number;
  locationName?: string;
  page?: number;
  practiceId?: number;
  practiceName?: string;
  providerId?: number;
  providerName?: string;
}

export interface Location {
  id: number;
  name: string;
}

/** Response shape for GET /locations/{locationId} */
export interface LocationDetail {
  locationId?: number;
  locationShortName?: string;
  locationName?: string;
  locationAddress1?: string;
  locationAddress2?: string;
  locationCity?: string;
  locationState?: string;
  locationZip?: string;
  locationPhone?: string;
  locationFax?: string;
  locationEmail?: string;
  locationExt?: string;
  locationWebsite?: string;
  locationComments?: string;
  posId?: number;
}

/** Request body for PATCH /locations/{locationId} */
export interface LocationUpdateRequest {
  locationShortName?: string;
  locationName?: string;
  locationAddress1?: string;
  locationAddress2?: string;
  locationCity?: string;
  locationState?: string;
  locationZip?: string;
  locationPhone?: string;
  locationFax?: string;
  locationEmail?: string;
  locationExt?: string;
  locationWebsite?: string;
  locationComments?: string;
  posId?: number;
}

/** Lab link item from GET /locations/{locationId}/labs */
export interface LocationLabItem {
  labId?: number;
  labCode?: string;
  locationLabLinkId?: number;
  locationLabAccountNo?: string;
  locationLabSiteId?: string;
  locationLabUserName?: string;
  locationLabPassword?: string;
  locationLabOrderPrefix?: string;
  isOrderDirectory?: boolean;
  orderDirectory?: string;
  enteredBy?: string;
  enteredDate?: string;
}

/** Response shape for GET /locations/{locationId}/labs */
export interface LocationLabsResponse {
  locationId?: number;
  labs?: LocationLabItem[];
}

/** Lab item for PUT /locations/{locationId}/labs request body */
export interface LocationLabPutItem {
  locationLabLinkId?: number;
  labCode?: string;
  locationLabAccountNo?: string;
  locationLabSiteId?: string;
  locationLabUserName?: string;
  locationLabPassword?: string;
  locationLabOrderPrefix?: string;
  isOrderDirectory?: boolean;
  orderDirectory?: string;
}

/** Request body for PUT /locations/{locationId}/labs */
export interface LocationLabsPutRequest {
  labs?: LocationLabPutItem[];
  deletedLocationLabLinkIds?: number[];
}

/** Lab item from GET /labs (for dropdown) */
export interface LabOption {
  labId: number;
  labCode: string;
}

/** Response shape for GET /labs */
export interface LabsListResponse {
  labs?: LabOption[];
}

export interface Provider {
  id: number;
  name: string;
}

/** Response shape for GET /providers/{providerId} (extended) */
export interface ProviderDetail {
  providerId?: number;
  providerShortName?: string;
  providerFirstName?: string;
  providerLastName?: string;
  providerTitle?: string;
  providerEmail?: string;
  providerFax?: string;
  providerPhone?: string;
  providerNPI?: string;
  providerLicenseNo?: string;
  providerSpecialty?: string;
  providerMI?: string;
  providerHomeAdd?: string;
  providerOfficeAdd?: string;
  providerCity?: string;
  providerState?: string;
  providerZip?: string;
  providerExt?: string;
  providerWebsite?: string;
  providerOfficeExt?: string;
  providerPager?: string;
  providerCell?: string;
  providerType?: string;
  providerIsActive?: boolean;
}

/** Request body for PATCH /providers/{providerId} */
export interface ProviderUpdateRequest {
  providerShortName?: string;
  providerFirstName?: string;
  providerLastName?: string;
  providerTitle?: string;
  providerEmail?: string;
  providerFax?: string;
  providerPhone?: string;
  providerNPI?: string;
  providerLicenseNo?: string;
  providerSpecialty?: string;
  providerMI?: string;
  providerHomeAdd?: string;
  providerOfficeAdd?: string;
  providerCity?: string;
  providerState?: string;
  providerZip?: string;
  providerExt?: string;
  providerWebsite?: string;
  providerOfficeExt?: string;
  providerPager?: string;
  providerCell?: string;
  /** API expects integer (e.g. 1=Doctor, 2=Nurse, 3=Medical Assistant). */
  providerType?: number;
  providerIsActive?: boolean;
}

/** Provider locations: GET response item and PUT body item (GET may include locationShortName for display) */
export interface ProviderLocationItem {
  providerLocationDetailId?: number;
  locationId?: number;
  locationShortName?: string;
  locationName?: string;
}

export interface ProviderLocationsPutRequest {
  locations?: ProviderLocationItem[];
  deletedProviderLocationDetailIds?: number[];
}

/** Provider practices: PUT body (shape may mirror locations) */
export interface ProviderPracticeItem {
  providerPracticeDetailId?: number;
  practiceId?: number;
}

export interface ProviderPracticesPutRequest {
  practices?: ProviderPracticeItem[];
  deletedProviderPracticeDetailIds?: number[];
}

/** Insurance PIN row: GET response and PUT body (GET may include locationShortName for display) */
export interface ProviderInsurancePinItem {
  providerInsurancePINId?: number;
  insuranceId?: number;
  locationId?: number;
  locationShortName?: string;
  locationName?: string;
  providerInsuIndividualPin?: string;
  providerInsuGroupPin?: string;
  providerInsuNetworkId?: string;
  providerInsuComments?: string;
  providerInsuElectronic?: boolean;
  providerInsuBillToProviderSsn?: boolean;
  providerInsuBillToHomeAddr?: boolean;
  providerInsu2310BLoop?: boolean;
}

export interface ProviderInsurancesPutRequest {
  insurances?: ProviderInsurancePinItem[];
  deletedProviderInsurancePINIds?: number[];
}

/** Provider EDI vendors: PUT body */
export interface ProviderVendorsPutRequest {
  vendorIds?: number[];
  deletedProviderVendorDetailIds?: number[];
}

/** EDI vendor option from GET /vendors */
export interface VendorOption {
  vendorId?: number;
  vendorName?: string;
  [key: string]: any;
}

/** Insurance option from GET /insurances (API may return insuranceCode, insuranceDesc) */
export interface InsuranceOption {
  insuranceId?: number;
  insuranceName?: string;
  insuranceCode?: string;
  insuranceDesc?: string;
  [key: string]: any;
}

/** Map API insurance item to InsuranceOption (use insuranceCode or insuranceDesc for display name). */
function toInsuranceOption(i: any): InsuranceOption {
  const name = (i?.insuranceCode ?? i?.insuranceDesc ?? i?.insuranceName ?? i?.name ?? '').trim() || String(i?.insuranceId ?? '');
  return {
    ...i,
    insuranceId: i?.insuranceId ?? i?.id,
    insuranceName: name
  };
}

/** State license row: GET response and PUT body item */
export interface ProviderStateLicenseItem {
  providerStateLicenseDetailId?: number;
  providerState?: string;
  providerLicense?: string;
  providerWCAuthNo?: string;
  providerWCRateCode?: string;
}

export interface ProviderStateLicensesPutRequest {
  stateLicenses?: ProviderStateLicenseItem[];
  deletedProviderStateLicenseDetailIds?: number[];
}

/** Additional info: GET response and PUT body (single record per provider) */
export interface ProviderAdditionalInfo {
  providerAddInfoUPIN?: string;
  providerAddInfoSSN?: string;
  providerAddInfoDEA?: string;
  providerAddInfoCLIA?: string;
  providerAddInfoNPI?: string;
  providerAddInfoMedicaidPIN?: string;
  providerAddInfoMedicarePIN?: string;
}

export interface Practice {
  id: number;
  name: string;
}

/** Response shape for GET /practices/{practiceId} */
export interface PracticeDetail {
  practiceId?: number;
  practiceShortName?: string;
  practiceName?: string;
  practiceFirstName?: string;
  practiceLastName?: string;
  practiceAddress1?: string;
  practiceAddress2?: string;
  practiceCity?: string;
  practiceState?: string;
  practiceZip?: string;
  practicePhone?: string;
  practiceFax?: string;
  practiceExt?: string;
  practiceEmail?: string;
  practiceWebsite?: string;
  practiceComments?: string;
  practiceElectronic?: boolean;
  practiceEIN?: string;
  practiceNPI?: string;
  practiceNPI2?: string;
  practiceNPIForEnzo?: string;
  isNPI2PrintOnHICFA?: boolean;
  practiceBillToProviderEIN?: boolean;
  practiceDEANumber?: string;
  practiceSPINumber?: string;
  is2010AB?: boolean;
  practiceMedicarePayerId?: string;
  practiceMedicaidPayerId?: string;
  practiceChampusPayerId?: string;
  practiceChampvaPayerId?: string;
  practiceGroupPayerId?: string;
  practiceFECAPayerId?: string;
  practiceBCBSPayerId?: string;
  practiceGHIMedicarePayerId?: string;
  specialityId?: number;
}

/** Request body for PATCH /practices/{practiceId} */
export interface PracticeUpdateRequest {
  practiceShortName?: string;
  practiceName?: string;
  practiceFirstName?: string;
  practiceLastName?: string;
  practiceAddress1?: string;
  practiceAddress2?: string;
  practiceCity?: string;
  practiceState?: string;
  practiceZip?: string;
  practicePhone?: string;
  practiceFax?: string;
  practiceExt?: string;
  practiceEmail?: string;
  practiceWebsite?: string;
  practiceComments?: string;
  practiceElectronic?: boolean;
  practiceEIN?: string;
  practiceNPI?: string;
  practiceNPI2?: string;
  practiceNPIForEnzo?: string;
  isNPI2PrintOnHICFA?: boolean;
  practiceBillToProviderEIN?: boolean;
  practiceDEANumber?: string;
  practiceSPINumber?: string;
  is2010AB?: boolean;
  practiceMedicarePayerId?: string;
  practiceMedicaidPayerId?: string;
  practiceChampusPayerId?: string;
  practiceChampvaPayerId?: string;
  practiceGroupPayerId?: string;
  practiceFECAPayerId?: string;
  practiceBCBSPayerId?: string;
  practiceGHIMedicarePayerId?: string;
  specialityId?: number;
}

export interface PracticeLocationProviderResponseData {
  limit?: number;
  locationId?: number;
  locationName?: string;
  locations?: Location[];
  message?: string;
  missingLinks?: string[];
  page?: number;
  practiceId?: number;
  practiceName?: string;
  practices?: Practice[];
  providerId?: number;
  providerName?: string;
  providers?: Provider[];
  total?: number;
  totalPages?: number;
  valid?: boolean;
  validatedLinks?: string[];
}

export interface PracticeLocationProviderResponse {
  status: string;
  message: string;
  data: PracticeLocationProviderResponseData;
}

@Injectable({
  providedIn: 'root'
})
export class ReferenceDataService {
  constructor(private apiService: ApiService) { }

  /**
   * GET /locations/{locationId} - Get location details by ID
   */
  getLocationById(locationId: number): Observable<LocationDetail> {
    return this.apiService.get<{ data?: LocationDetail } & LocationDetail>(`locations/${locationId}`).pipe(
      map((response) => response?.data ?? response),
      catchError(() => of({} as LocationDetail))
    );
  }

  /**
   * PATCH /locations/{locationId} - Update location details
   */
  updateLocation(locationId: number, body: LocationUpdateRequest): Observable<any> {
    return this.apiService.patch<any>(`locations/${locationId}`, body);
  }

  /**
   * DELETE /locations/{locationId} - Delete a location
   */
  deleteLocation(locationId: number): Observable<any> {
    return this.apiService.delete<any>(`locations/${locationId}`);
  }

  /**
   * GET /locations - List all locations for dropdowns
   */
  getLocations(): Observable<Location[]> {
    return this.apiService.get<any>('locations').pipe(
      map((response) => {
        if (!response || typeof response !== 'object') {
          return [] as Location[];
        }

        // API shape: { status, message, data: [...] }
        const data = (response as any).data;
        const rows = Array.isArray(data) ? data : Array.isArray(response) ? (response as any[]) : [];

        return rows.map((row: any) => {
          const id = row.locationId ?? row.id;
          const name =
            (row.locationShortName ?? row.locationName ?? '').toString().trim() ||
            (id != null ? String(id) : '');
          return { id, name } as Location;
        }).filter((loc: Location) => loc.id != null);
      }),
      catchError(() => of([] as Location[]))
    );
  }

  /**
   * GET /labs - List all labs for dropdown (tblLab: LabId, LabCode)
   */
  getLabs(): Observable<LabsListResponse> {
    return this.apiService.get<{ data?: LabsListResponse; success?: boolean } & LabsListResponse>('labs').pipe(
      map((response) => response?.data ?? response),
      catchError(() => of({ labs: [] }))
    );
  }

  /**
   * GET /locations/{locationId}/labs - Get lab links for a location
   */
  getLocationLabs(locationId: number): Observable<LocationLabsResponse> {
    return this.apiService.get<{ data?: LocationLabsResponse } & LocationLabsResponse>(`locations/${locationId}/labs`).pipe(
      map((response) => response?.data ?? response),
      catchError(() => of({ labs: [] }))
    );
  }

  /**
   * PUT /locations/{locationId}/labs - Update location lab list
   */
  putLocationLabs(locationId: number, body: LocationLabsPutRequest): Observable<any> {
    return this.apiService.put<any>(`locations/${locationId}/labs`, body);
  }

  /**
   * DELETE /locations/{locationId}/labs/{locationLabLinkId}
   */
  deleteLocationLab(locationId: number, locationLabLinkId: number): Observable<any> {
    return this.apiService.delete<any>(`locations/${locationId}/labs/${locationLabLinkId}`);
  }

  /**
   * GET /practices/{practiceId} - Get practice details by ID
   */
  getPracticeById(practiceId: number): Observable<PracticeDetail> {
    return this.apiService.get<{ data?: PracticeDetail } & PracticeDetail>(`practices/${practiceId}`).pipe(
      map((response) => response?.data ?? response),
      catchError(() => of({} as PracticeDetail))
    );
  }

  /**
   * PATCH /practices/{practiceId} - Update practice details
   */
  updatePractice(practiceId: number, body: PracticeUpdateRequest): Observable<any> {
    return this.apiService.patch<any>(`practices/${practiceId}`, body);
  }

  /**
   * DELETE /practices/{practiceId} - Delete a practice
   */
  deletePractice(practiceId: number): Observable<any> {
    return this.apiService.delete<any>(`practices/${practiceId}`);
  }

  /**
   * GET /api/v1/practices - List all practices (view all)
   * Optional: query (search by practice name/short name), page, pageSize
   */
  listPractices(query?: string, page: number = 1, pageSize: number = 200): Observable<{
    practices: Array<{ practiceId: number; practiceName?: string; practiceShortName?: string }>;
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    const params: Record<string, string | number> = { page, pageSize };
    if (query != null && String(query).trim() !== '') {
      params['query'] = String(query).trim();
    }
    return this.apiService.get<any>('practices', { params }).pipe(
      map((r: any) => {
        const d = r?.data ?? r;
        const practices = d?.practices ?? [];
        return {
          practices: Array.isArray(practices) ? practices : [],
          totalCount: d?.totalCount ?? 0,
          page: d?.page ?? page,
          pageSize: d?.pageSize ?? pageSize,
          totalPages: d?.totalPages ?? 1
        };
      }),
      catchError(() => of({ practices: [], totalCount: 0, page: 1, pageSize: 50, totalPages: 1 }))
    );
  }

  /**
   * GET /api/v1/providers - List all providers (view all)
   * Optional: query (search by short name, first name, last name), page, pageSize
   */
  listProviders(query?: string, page: number = 1, pageSize: number = 200): Observable<{
    providers: Array<{
      providerId: number;
      providerShortName?: string;
      providerFirstName?: string;
      providerLastName?: string;
    }>;
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    const params: Record<string, string | number> = { page, pageSize };
    if (query != null && String(query).trim() !== '') {
      params['query'] = String(query).trim();
    }
    return this.apiService.get<any>('providers', { params }).pipe(
      map((r: any) => {
        const d = r?.data ?? r;
        const providers = d?.providers ?? [];
        return {
          providers: Array.isArray(providers) ? providers : [],
          totalCount: d?.totalCount ?? 0,
          page: d?.page ?? page,
          pageSize: d?.pageSize ?? pageSize,
          totalPages: d?.totalPages ?? 1
        };
      }),
      catchError(() => of({ providers: [], totalCount: 0, page: 1, pageSize: 50, totalPages: 1 }))
    );
  }

  /**
   * GET /providers/{providerId} - Get provider details by ID
   */
  getProviderById(providerId: number): Observable<ProviderDetail> {
    return this.apiService.get<{ data?: ProviderDetail } & ProviderDetail>(`providers/${providerId}`).pipe(
      map((response) => response?.data ?? response),
      catchError(() => of({} as ProviderDetail))
    );
  }

  /**
   * PATCH /providers/{providerId} - Update provider details
   */
  updateProvider(providerId: number, body: ProviderUpdateRequest): Observable<any> {
    return this.apiService.patch<any>(`providers/${providerId}`, body);
  }

  /**
   * DELETE /providers/{providerId} - Delete a provider
   */
  deleteProvider(providerId: number): Observable<any> {
    return this.apiService.delete<any>(`providers/${providerId}`);
  }

  /**
   * GET /providers/{providerId}/locations - List locations linked to the provider.
   * API may return array, { data: [...] }, or { data: { locations: [...] } }.
   */
  getProviderLocations(providerId: number): Observable<ProviderLocationItem[]> {
    return this.apiService.get<any>(`providers/${providerId}/locations`).pipe(
      map((r) => {
        if (!r || typeof r !== 'object') return [];
        if (Array.isArray(r)) return r as ProviderLocationItem[];
        const data = (r as any).data;
        if (Array.isArray(data)) return data as ProviderLocationItem[];
        if (data && typeof data === 'object' && Array.isArray(data.locations)) return data.locations as ProviderLocationItem[];
        return [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * PUT /providers/{providerId}/locations - Add/remove locations
   */
  putProviderLocations(providerId: number, body: ProviderLocationsPutRequest): Observable<any> {
    return this.apiService.put<any>(`providers/${providerId}/locations`, body);
  }

  /**
   * GET /providers/{providerId}/practices - List practices for provider
   */
  getProviderPractices(providerId: number): Observable<ProviderPracticeItem[]> {
    return this.apiService.get<{ data?: ProviderPracticeItem[] } | ProviderPracticeItem[]>(`providers/${providerId}/practices`).pipe(
      map((r) => (Array.isArray(r) ? r : (r as any)?.data ?? [])),
      catchError(() => of([]))
    );
  }

  /**
   * PUT /providers/{providerId}/practices - Add/remove practices
   */
  putProviderPractices(providerId: number, body: ProviderPracticesPutRequest): Observable<any> {
    return this.apiService.put<any>(`providers/${providerId}/practices`, body);
  }

  /**
   * GET /insurances - All insurances (for Insurance dropdown).
   * API returns { status, message, data: { insurances: [{ insuranceId, insuranceCode, insuranceDesc }] } }.
   */
  getInsurances(): Observable<InsuranceOption[]> {
    return this.apiService.get<any>(`insurances`).pipe(
      map((r) => {
        if (!r || typeof r !== 'object') return [];
        if (Array.isArray(r)) return r.map((i: any) => toInsuranceOption(i));
        const arr = (r.data?.insurances ?? r.insurances ?? (Array.isArray(r.data) ? r.data : [])) as any[];
        return Array.isArray(arr) ? arr.map((i: any) => toInsuranceOption(i)) : [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * GET /providers/{providerId}/insurances - List insurance PIN rows
   */
  getProviderInsurances(providerId: number): Observable<ProviderInsurancePinItem[]> {
    return this.apiService.get<{ data?: ProviderInsurancePinItem[] } | ProviderInsurancePinItem[]>(`providers/${providerId}/insurances`).pipe(
      map((r) => {
        if (!r || typeof r !== 'object') return [];
        // Handle array response
        if (Array.isArray(r)) return r;
        // Handle nested structure: { data: { insurances: [...] } }
        if ((r as any).data && typeof (r as any).data === 'object') {
          const dataObj = (r as any).data;
          if (Array.isArray(dataObj.insurances)) {
            return dataObj.insurances;
          }
          if (Array.isArray(dataObj)) {
            return dataObj;
          }
        }
        // Handle flat structure: { insurances: [...] }
        if (Array.isArray((r as any).insurances)) {
          return (r as any).insurances;
        }
        return [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * PUT /providers/{providerId}/insurances - Create/update/delete insurance PINs
   */
  putProviderInsurances(providerId: number, body: ProviderInsurancesPutRequest): Observable<any> {
    return this.apiService.put<any>(`providers/${providerId}/insurances`, body);
  }

  /**
   * GET /vendors - All EDI vendors (for EDI Vendors dropdown/checkboxes)
   */
  getVendors(): Observable<VendorOption[]> {
    return this.apiService.get<{ data?: VendorOption[] } | VendorOption[]>(`vendors`).pipe(
      map((r) => (Array.isArray(r) ? r : (r as any)?.data ?? (r as any)?.vendors ?? [])),
      catchError(() => of([]))
    );
  }

  /**
   * GET /providers/{providerId}/vendors - List EDI vendors for provider
   */
  getProviderVendors(providerId: number): Observable<{ providerVendorDetailId?: number; vendorId?: number }[]> {
    return this.apiService.get<any>(`providers/${providerId}/vendors`).pipe(
      map((r) => (Array.isArray(r) ? r : (r as any)?.data ?? (r as any)?.vendors ?? [])),
      catchError(() => of([]))
    );
  }

  /**
   * PUT /providers/{providerId}/vendors - Add/remove EDI vendors
   */
  putProviderVendors(providerId: number, body: ProviderVendorsPutRequest): Observable<any> {
    return this.apiService.put<any>(`providers/${providerId}/vendors`, body);
  }

  /**
   * GET /providers/{providerId}/state-licenses - List state license rows
   */
  getProviderStateLicenses(providerId: number): Observable<ProviderStateLicenseItem[]> {
    return this.apiService.get<{ data?: ProviderStateLicenseItem[] } | ProviderStateLicenseItem[]>(`providers/${providerId}/state-licenses`).pipe(
      map((r) => {
        if (!r || typeof r !== 'object') return [];
        // Handle array response
        if (Array.isArray(r)) return r;
        // Handle nested structure: { data: { stateLicenses: [...] } }
        if ((r as any).data && typeof (r as any).data === 'object') {
          const dataObj = (r as any).data;
          if (Array.isArray(dataObj.stateLicenses)) {
            return dataObj.stateLicenses;
          }
          if (Array.isArray(dataObj)) {
            return dataObj;
          }
        }
        // Handle flat structure: { stateLicenses: [...] }
        if (Array.isArray((r as any).stateLicenses)) {
          return (r as any).stateLicenses;
        }
        return [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * PUT /providers/{providerId}/state-licenses - Add/update/remove state licenses
   */
  putProviderStateLicenses(providerId: number, body: ProviderStateLicensesPutRequest): Observable<any> {
    return this.apiService.put<any>(`providers/${providerId}/state-licenses`, body);
  }

  /**
   * DELETE /providers/{providerId}/state-licenses/{providerStateLicenseDetailId}
   */
  deleteProviderStateLicense(providerId: number, providerStateLicenseDetailId: number): Observable<any> {
    return this.apiService.delete<any>(`providers/${providerId}/state-licenses/${providerStateLicenseDetailId}`);
  }

  /**
   * DELETE /providers/{providerId}/insurances/{providerInsurancePINId}
   */
  deleteProviderInsurancePin(providerId: number, providerInsurancePINId: number): Observable<any> {
    return this.apiService.delete<any>(`providers/${providerId}/insurances/${providerInsurancePINId}`);
  }

  /**
   * GET /providers/{providerId}/additional-info - Get additional info (single record)
   */
  getProviderAdditionalInfo(providerId: number): Observable<ProviderAdditionalInfo> {
    return this.apiService.get<any>(`providers/${providerId}/additional-info`).pipe(
      map((r) => {
        if (!r || typeof r !== 'object') {
          return {};
        }
        const hasPayload = (o: any) => o && typeof o === 'object' && ('providerAddInfoUPIN' in o || 'providerAddInfoNPI' in o || 'providerAddInfoSSN' in o);
        if (hasPayload(r)) return r as ProviderAdditionalInfo;
        const inner = r.data ?? r.result ?? r.additionalInfo;
        if (hasPayload(inner)) return inner as ProviderAdditionalInfo;
        if (inner && typeof inner === 'object' && inner.data && hasPayload(inner.data)) return inner.data as ProviderAdditionalInfo;
        return {};
      }),
      catchError(() => of({}))
    );
  }

  /**
   * PUT /providers/{providerId}/additional-info - Create or update additional info
   */
  putProviderAdditionalInfo(providerId: number, body: ProviderAdditionalInfo): Observable<any> {
    return this.apiService.put<any>(`providers/${providerId}/additional-info`, body);
  }

  /**
   * Search locations by name
   */
  searchLocations(searchTerm: string, limit: number = 20): Observable<Location[]> {
    const request: PracticeLocationProviderRequest = {
      locationName: searchTerm,
      limit: limit,
      page: 0
    };

    return this.apiService.post<PracticeLocationProviderResponse>(
      'validation/practice-location-provider',
      request
    ).pipe(
      map((response) => {
        return response.data?.locations || [];
      }),
      catchError(() => {
        return of([]);
      })
    );
  }

  /**
   * Search providers by name
   */
  searchProviders(searchTerm: string, limit: number = 20): Observable<Provider[]> {
    const request: PracticeLocationProviderRequest = {
      providerName: searchTerm,
      limit: limit,
      page: 0
    };

    return this.apiService.post<PracticeLocationProviderResponse>(
      'validation/practice-location-provider',
      request
    ).pipe(
      map((response) => {
        return response.data?.providers || [];
      }),
      catchError(() => {
        return of([]);
      })
    );
  }

  /**
   * Search practices by name
   */
  searchPractices(searchTerm: string, locationId?: number, limit: number = 20): Observable<Practice[]> {
    const request: PracticeLocationProviderRequest = {
      practiceName: searchTerm,
      limit: limit,
      page: 0
    };

    // If locationId is provided, filter practices by location
    if (locationId) {
      request.locationId = locationId;
    }

    return this.apiService.post<PracticeLocationProviderResponse>(
      'validation/practice-location-provider',
      request
    ).pipe(
      map((response) => {
        return response.data?.practices || [];
      }),
      catchError(() => {
        return of([]);
      })
    );
  }

  /**
   * Get practices by location ID
   */
  getPracticesByLocation(locationId: number, limit: number = 20): Observable<Practice[]> {
    const request: PracticeLocationProviderRequest = {
      locationId: locationId,
      limit: limit,
      page: 0
    };

    return this.apiService.post<PracticeLocationProviderResponse>(
      'validation/practice-location-provider',
      request
    ).pipe(
      map((response) => {
        return response.data?.practices || [];
      }),
      catchError(() => {
        return of([]);
      })
    );
  }

  /**
   * Generic method to search practice-location-provider
   */
  searchPracticeLocationProvider(request: PracticeLocationProviderRequest): Observable<PracticeLocationProviderResponseData> {
    return this.apiService.post<PracticeLocationProviderResponse>(
      'validation/practice-location-provider',
      request
    ).pipe(
      map((response: any) => {
        return response.data || response; // Fallback if data wrapper is missing
      }),
      catchError(() => {
        return of({} as PracticeLocationProviderResponseData);
      })
    );
  }
}
